import { useState } from "react";
import { Box, Divider, Stack } from "@mui/material";
import { AnimatePresence, motion, HTMLMotionProps } from "motion/react";
import { useTranslation } from "next-i18next";
import { IErrorResponse } from "src/api/client/types";
import { useWatchlistWikifolio } from "src/api/client/watchlist.api";
import { IconInfoOuterCircle } from "src/components/icons-mui/info-outer-circle";
import { Button } from "src/components-mui/base/button/button";
import { OverlayLoadingSpinner } from "src/components-mui/base/overlay-loading-spinner/overlay-loading-spinner";
import { Typography } from "src/components-mui/base/typography/typography";
import { PopoverButtonIconInfo } from "src/components-mui/base/wf-popover/popover-button-icon-info";
import { WfPopover } from "src/components-mui/base/wf-popover/wf-popover";
import { WfSlider } from "src/components-mui/base/wf-slider/wf-slider";
import { CBDetailsAccordion } from "src/components-mui/pages/detail/certificate-box/components/details-accordion";
import { CBFooter } from "src/components-mui/pages/detail/certificate-box/components/footer";
import { CBHeaderNotInvestible } from "src/components-mui/pages/detail/certificate-box/components/header/header-not-investible";
import { InfoBox } from "src/components-mui/pages/detail/certificate-box/components/info-box";
import { CBPrices } from "src/components-mui/pages/detail/certificate-box/components/prices";
import { CBProgressBar } from "src/components-mui/pages/detail/certificate-box/components/progress-bar";
import { CBRatioTabBar } from "src/components-mui/pages/detail/certificate-box/components/ratio-tab-bar";
import { SpacedContainer } from "src/components-mui/pages/detail/certificate-box/components/spaced-container";
import { CBCertificateFee, CBPerformanceFee } from "src/components-mui/pages/detail/certificate-box/components/statistics";
import { useCertificateBoxContext } from "src/components-mui/pages/detail/certificate-box/context";
import { getGlobals } from "src/constants/globals";
import { useNumberFormatter } from "src/hooks/formatters/use-number-formatter";
import { useLinkBuilder } from "src/hooks/use-link-builder";
import { useWfToast, ToastStatus } from "src/hooks/use-wf-toast";
import { useUserStore } from "src/stores/user-store";

const INVESTMENT_MIN = 100;
const INVESTMENT_MAX = 5000;
const INVESTMENT_STEP = 100;

const PREBOOK_MOTION_BOX_NOTWATCHLISTED_TEST_ID = "prebook-motion-box-notwatchlisted";
const PREBOOK_MOTION_BOX_WATCHLISTED_TEST_ID = "prebook-motion-box-watchlisted";

const PREBOOK_BUTTON_TEST_ID = "prebook-button";

const MotionBox = motion.div;

const PrebookMotionBox = (props: Omit<HTMLMotionProps<"div">, "ref">) => (
    <MotionBox
        style={{
            position: "relative",
            overflow: "hidden",
            marginLeft: "-16px",
            marginRight: "-16px",
            paddingLeft: "16px",
            paddingRight: "16px",
        }}
        variants={{
            open: {
                height: "auto",
                opacity: 1,
            },
            closed: {
                height: "0px",
                opacity: 0,
            },
        }}
        initial="closed"
        animate="open"
        exit="closed"
        transition={{ bounce: 0, duration: 0.4 }}
        {...props}
    />
);

export const CertificateBoxPublished = () => {
    const { t } = useTranslation("common");
    const { openLoginModal } = useLinkBuilder();
    const toast = useWfToast();
    const { isCurrentUser, isLoggedIn } = useUserStore();
    const {
        dict,
        data: { trader, simplifiedExchangeRate, wikifolio, certificates, tooltips },
        selectedCertificate: certificate,
    } = useCertificateBoxContext();

    const {
        isOnWatchlist,
        add: addWatchlist,
        remove: removeWatchlist,
    } = useWatchlistWikifolio({ wikifolioId: wikifolio.id, isOnWatchlistFromServer: wikifolio.isOnWatchlist });

    const { formatNumber } = useNumberFormatter();

    const prebookValues = {
        min: INVESTMENT_MIN * simplifiedExchangeRate,
        max: INVESTMENT_MAX * simplifiedExchangeRate,
        step: INVESTMENT_STEP * simplifiedExchangeRate,
    };

    const [buyingInterests, setBuyingInterests] = useState(certificate.buyingInterests!.currentValue);
    const [reservedCapital, setReservedCapital] = useState(certificate.reservedCapital!.currentValue);
    const [intendedInvestment, setIntendedInvestment] = useState((wikifolio.intendedInvestment || INVESTMENT_MIN) * simplifiedExchangeRate);
    const [isLoading, setLoading] = useState(false);

    const alwaysShowOperationalMessage = certificate.hasBlockedReason || !trader.isLegitimized;
    const showOperationalMessage = dict.operationalMessage && (alwaysShowOperationalMessage || !isOnWatchlist);

    const onPrebook = async () => {
        if (!isLoggedIn) {
            void openLoginModal();
            return;
        }

        try {
            setLoading(true);
            await addWatchlist({ intendedInvestment: intendedInvestment / simplifiedExchangeRate });
            setReservedCapital(reservedCapital + intendedInvestment);
            setBuyingInterests(buyingInterests + 1);
            toast({ status: ToastStatus.Success, title: t("add-wikifolio-success") });
        } catch (result) {
            setLoading(false);
            toast({ status: ToastStatus.Error, title: (result as IErrorResponse)?.message || t("general-error") });
        }
    };

    const onUndoPrebook = async () => {
        try {
            setLoading(true);
            await removeWatchlist();
            setReservedCapital(reservedCapital - intendedInvestment);
            setBuyingInterests(buyingInterests - 1);
            toast({ status: ToastStatus.Success, title: t("remove-wikifolio-success") });
        } catch (result) {
            setLoading(false);
            toast({ status: ToastStatus.Error, title: (result as IErrorResponse)?.message || t("general-error") });
        }
    };

    return (
        <>
            <CBHeaderNotInvestible tooltip={tooltips.previewMode} />
            <SpacedContainer sx={{ pb: 0 }}>
                {certificates.length > 1 && <CBRatioTabBar />}
                <CBPrices />
                <Divider />

                <CBCertificateFee />
                <CBPerformanceFee />
                <Divider />
            </SpacedContainer>

            <CBDetailsAccordion buttonContainerSx={{ pr: 2 }}>
                <Typography
                    sx={{
                        px: 2,
                        pb: 2,
                        fontSize: "xs",
                    }}
                >
                    {dict.publishedCriteriaParagraph}
                </Typography>
                <Stack spacing={0.5}>
                    <CBProgressBar {...certificate.testPhaseDays!} description={dict.publishedTestPhase} />
                    <CBProgressBar
                        currentValue={buyingInterests}
                        requiredValue={certificate.buyingInterests!.requiredValue}
                        description={dict.publishedBuyingInterests}
                    />
                    <CBProgressBar
                        prefix={getGlobals().currency}
                        currentValue={reservedCapital}
                        requiredValue={certificate.reservedCapital!.requiredValue}
                        description={dict.publishedReservedCapital}
                    />
                </Stack>
                {showOperationalMessage && !isCurrentUser(trader) && (
                    <Box
                        sx={{
                            px: 2,
                            pt: 2,
                        }}
                    >
                        <InfoBox icon={<IconInfoOuterCircle sx={{ mt: 0.2, width: "18px", height: "18px" }} />}>
                            <Typography
                                sx={{
                                    fontSize: "sm",
                                }}
                            >
                                {dict.operationalMessage}
                            </Typography>
                        </InfoBox>
                    </Box>
                )}
            </CBDetailsAccordion>

            <SpacedContainer>
                {!isCurrentUser(trader) && (
                    <AnimatePresence mode="wait" onExitComplete={() => setLoading(false)}>
                        {!isOnWatchlist && (
                            <PrebookMotionBox key="notwatchlisted" data-test-id={PREBOOK_MOTION_BOX_NOTWATCHLISTED_TEST_ID}>
                                <Stack spacing={3}>
                                    <Stack
                                        spacing={1}
                                        sx={{
                                            pt: 1,
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <Typography
                                                sx={{
                                                    fontWeight: "bold",
                                                    fontSize: "sm",
                                                }}
                                            >
                                                {dict.prebookTitle}
                                            </Typography>

                                            <WfPopover
                                                content={tooltips.emissionRequired!.content}
                                                link={tooltips.emissionRequired!.link}
                                                PopoverTrigger={<PopoverButtonIconInfo ariaLabel={dict.prebookTitle} sx={{ ml: 0.5 }} />}
                                            />
                                        </Box>

                                        <Typography
                                            sx={{
                                                fontSize: "xs",
                                            }}
                                        >
                                            {dict.prebookParagraph}
                                        </Typography>
                                    </Stack>

                                    <Stack spacing={2}>
                                        <Typography>
                                            {getGlobals().currency} <strong>{formatNumber(intendedInvestment)}</strong>
                                        </Typography>

                                        <WfSlider size="small" {...prebookValues} value={intendedInvestment} onChange={setIntendedInvestment} />

                                        <Button
                                            fullWidth
                                            color="green"
                                            size="medium"
                                            variant="contained"
                                            onClick={onPrebook}
                                            data-test-id={PREBOOK_BUTTON_TEST_ID}
                                        >
                                            {dict.prebookButton}
                                        </Button>
                                    </Stack>
                                </Stack>

                                {isLoading && <OverlayLoadingSpinner />}
                            </PrebookMotionBox>
                        )}

                        {isOnWatchlist && (
                            <PrebookMotionBox key="watchlisted" data-test-id={PREBOOK_MOTION_BOX_WATCHLISTED_TEST_ID}>
                                <Stack spacing={2}>
                                    <Stack
                                        spacing={1}
                                        sx={{
                                            py: 1,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: "sm",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            {dict.prebookDoneTitle}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: "xs",
                                            }}
                                        >
                                            {dict.prebookDoneParagraph}
                                        </Typography>
                                    </Stack>
                                    <Button fullWidth size="medium" variant="outlined" onClick={onUndoPrebook} data-test-id={PREBOOK_BUTTON_TEST_ID}>
                                        {dict.prebookDoneButton}
                                    </Button>
                                </Stack>
                                {isLoading && <OverlayLoadingSpinner />}
                            </PrebookMotionBox>
                        )}
                    </AnimatePresence>
                )}

                <CBFooter />
            </SpacedContainer>
        </>
    );
};
